import type { FlagGameSession, FlagDifficulty, FlagGameModeType, FlagItem, FlagRoundResult } from '../types/flag.ts';
import { getLocalized } from './i18n.ts';
import { acceptedAnswers, normalizeText } from './text.ts';

export class FlagGameEngine {
  private session: FlagGameSession;
  private pool: FlagItem[] = [];
  private questions: FlagItem[] = [];
  private currentQuestionIndex: number = 0;

  constructor() {
    this.session = this.createEmptySession();
  }

  // ── Public API ──

  getSession(): Readonly<FlagGameSession> {
    return this.session;
  }

  /** Initialize and start a new flags game */
  async startGame(
    scenarioId: string,
    difficulty: FlagDifficulty,
    mode: FlagGameModeType
  ): Promise<void> {
    // Fetch flag dataset
    let dataFile = '';
    if (scenarioId === 'flags-world') dataFile = 'world.json';
    else if (scenarioId === 'flags-europe') dataFile = 'europe.json';
    else if (scenarioId === 'flags-spain') dataFile = 'spain.json';
    else if (scenarioId === 'flags-fun') dataFile = 'fun.json';
    else throw new Error(`Unknown flag scenario: ${scenarioId}`);

    const res = await fetch(`/data/flags/${dataFile}`);
    const allFlags: FlagItem[] = await res.json();

    // Filter flags based on difficulty
    // Easy: easy only. Medium: easy + medium. Hard: all.
    if (difficulty === 'easy') {
      this.pool = allFlags.filter(f => f.difficulty === 'easy');
    } else if (difficulty === 'medium') {
      this.pool = allFlags.filter(f => f.difficulty === 'easy' || f.difficulty === 'medium');
    } else {
      this.pool = allFlags;
    }

    // Shuffle the pool and select 10 unique flags for the game
    const shuffledPool = this.shuffle([...this.pool]);
    this.questions = shuffledPool.slice(0, Math.min(10, shuffledPool.length));
    this.currentQuestionIndex = 0;

    // Reset session
    this.session = {
      scenarioId,
      isFlags: true,
      state: 'COUNTDOWN',
      difficulty,
      mode,
      currentRound: 0,
      totalRounds: this.questions.length,
      rounds: [],
      totalPoints: 0,
      currentQuestion: null,
      options: [],
      timeRemaining: 15, // 15 seconds per round
    };

    this.setupNextQuestion();
  }

  /** Set the state to PLAYING when countdown completes */
  countdownDone(): void {
    this.session.state = 'PLAYING';
    this.session.timeRemaining = 15;
  }

  /** Update remaining time */
  updateTime(remaining: number): void {
    this.session.timeRemaining = remaining;
  }

  /** Handle multiple choice selection */
  handleChoice(answerCode: string): FlagRoundResult {
    const isCorrect = this.session.currentQuestion!.code === answerCode;
    const points = isCorrect ? this.calculateScore(this.session.timeRemaining) : 0;

    const result: FlagRoundResult = {
      flag: this.session.currentQuestion!,
      correct: isCorrect,
      userAnswer: answerCode,
      points,
      timeRemaining: this.session.timeRemaining,
    };

    this.recordRound(result);
    return result;
  }

  /** Handle written text answer */
  handleTextAnswer(text: string): FlagRoundResult {
    const question = this.session.currentQuestion!;
    const normalizedUser = normalizeText(text);

    // Right if it matches a name in any language, or one of the flag's aliases
    const isCorrect = normalizedUser !== '' &&
      acceptedAnswers(question.name, question.aliases).some(answer => normalizeText(answer) === normalizedUser);

    const points = isCorrect ? this.calculateScore(this.session.timeRemaining) : 0;

    const result: FlagRoundResult = {
      flag: this.session.currentQuestion!,
      correct: isCorrect,
      userAnswer: text,
      points,
      timeRemaining: this.session.timeRemaining,
    };

    this.recordRound(result);
    return result;
  }

  /** Handle round timeout */
  handleTimeout(): FlagRoundResult {
    const result: FlagRoundResult = {
      flag: this.session.currentQuestion!,
      correct: false,
      userAnswer: '',
      points: 0,
      timeRemaining: 0,
    };

    this.recordRound(result);
    return result;
  }

  /** Move to the next round or show results */
  nextRound(): void {
    if (this.session.currentRound >= this.session.totalRounds) {
      this.session.state = 'RESULTS';
    } else {
      this.setupNextQuestion();
      this.session.state = 'COUNTDOWN';
    }
  }

  /** Reset session and go back to IDLE */
  goHome(): void {
    this.session = this.createEmptySession();
  }

  /** Replay same game setup */
  async replay(): Promise<void> {
    await this.startGame(
      this.session.scenarioId,
      this.session.difficulty,
      this.session.mode
    );
  }

  /** Retrieve the list of all localized names in the current pool for autocomplete suggestions */
  getAutocompletePool(): string[] {
    return this.pool.map(f => getLocalized(f.name));
  }

  // ── Private Helpers ──

  private setupNextQuestion(): void {
    const question = this.questions[this.currentQuestionIndex]!;
    this.session.currentQuestion = question;

    if (this.session.mode === 'test') {
      // Pick 3 incorrect options from the pool (excluding the correct one)
      const distractors = this.shuffle(
        this.pool.filter(f => f.code !== question.code)
      ).slice(0, 3);

      // Mix them together with the correct one
      this.session.options = this.shuffle([question, ...distractors]);
    } else {
      this.session.options = [];
    }
  }

  private recordRound(result: FlagRoundResult): void {
    this.session.rounds.push(result);
    this.session.totalPoints += result.points;
    this.session.currentRound++;
    this.currentQuestionIndex++;
    this.session.state = 'FEEDBACK';
  }

  private calculateScore(timeRemaining: number): number {
    // 100 points base for correct answer + up to 100 extra points for fast answer
    // Max time is 15 seconds, so timeRemaining ratio is: timeRemaining / 15
    const timeBonus = Math.round((timeRemaining / 15) * 100);
    return 100 + timeBonus;
  }

  private shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j]!, array[i]!];
    }
    return array;
  }

  private createEmptySession(): FlagGameSession {
    return {
      scenarioId: '',
      isFlags: false,
      state: 'IDLE',
      difficulty: 'easy',
      mode: 'test',
      currentRound: 0,
      totalRounds: 0,
      rounds: [],
      totalPoints: 0,
      currentQuestion: null,
      options: [],
      timeRemaining: 0,
    };
  }
}
